import React, {
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import { createContext } from 'use-context-selector';

import PropTypes from 'prop-types';

import { toastNotification, fixGridAndFilterBuilder, getResourceTo } from 'react/utils/utils';
import { getSafeURL, setDefaultPinnedMenu } from 'react/containers/layout/sidebar/utils';

export const SidebarContext = createContext({});

export function SidebarProvider({ children, menus: menusInState }) {
  const [isSearching, setIsSearching] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovering] = useState(false);

  const [menus, setMenus] = useState(() =>  menusInState.map((item) => ({ ...item, active: false })));

  const [pinnedMenus, setPinnedMenus] = useState(() => {
    const storagePosition = new Greenmile.Storage.StoreMapPosition();

    const pinnedMenusOnStorage = storagePosition.getPinnedMenusFromUser();

    if (pinnedMenusOnStorage.length) {
      return pinnedMenusOnStorage;
    }

    return setDefaultPinnedMenu(menus);
  });

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((oldState) => !oldState);
    setIsHovering(false);

    setTimeout(() => {
      fixGridAndFilterBuilder();
    }, 500);
  }, []);

  const setActiveMenu = useCallback((menu) => {
    const isActive = menu?.active;

    if (isActive) {
      setMenus((oldState) => oldState.map((item) => ({ ...item, active: false })));
    } else {
      setMenus(
        (oldState) => oldState
          .map((item) => ({ ...item, active: item.name === menu.name }))
      );
    }
  }, []);

  const removeActiveMenu = useCallback(() => {
    setMenus(
      (oldState) => oldState
        .map((item) => ({ ...item, active: false }))
    );
  }, []);

  const setHover = useCallback((state) => {
    setIsHovering(state);
  }, []);

  const pinMenu = useCallback((menu) => {
    const isItPinned = pinnedMenus.find((pinnedMenu) => pinnedMenu.name === menu.name);
    const storagePosition = new Greenmile.Storage.StoreMapPosition();

    if (pinnedMenus.length === 5 && !isItPinned) {
      toastNotification.addToast({
        id: 'pinnedMenusLimit',
        type: 'danger',
        data: {
          title: getResourceTo('sidebar.pinnedMenusLimitToast.title'),
          description: getResourceTo('sidebar.pinnedMenusLimitToast.description')
        }
      });

      return;
    }

    if (isItPinned) {
      const updatedPinnedMenus = pinnedMenus.filter((pinnedMenu) => pinnedMenu.name !== menu.name);

      storagePosition.setPinnedMenusFromUser(updatedPinnedMenus);
      setPinnedMenus(updatedPinnedMenus);
    } else {
      const updatedPinnedMenus = [...pinnedMenus, menu];

      storagePosition.setPinnedMenusFromUser(updatedPinnedMenus);
      setPinnedMenus(updatedPinnedMenus);

      toastNotification.addToast({
        id: 'successPinMenu',
        type: 'success',
        data: {
          title: getResourceTo('sidebar.successPinMenuToast.title'),
          description: getResourceTo('sidebar.successPinMenuToast.description')
        }
      });
    }
  }, [pinnedMenus]);

  const isPinned = useCallback(
    (menu) => pinnedMenus
      .find((item) => item.name === menu.name || getSafeURL(item.url) === menu.url),
    [pinnedMenus]
  );

  const canPinMenus = useMemo(() => pinnedMenus?.length < 5, [pinnedMenus]);

  const value = useMemo(() => ({
    menus,
    isCollapsed,
    isHovered,
    pinnedMenus,
    canPinMenus,
    isSearching,
    setIsSearching,
    toggleSidebar,
    setActiveMenu,
    setHover,
    pinMenu,
    isPinned
  }), [
    menus,
    isCollapsed,
    isHovered,
    pinnedMenus,
    canPinMenus,
    isSearching,
    JSON.stringify(menusInState),
    setIsSearching,
    toggleSidebar,
    setActiveMenu,
    setHover,
    pinMenu,
    isPinned
  ]);

  useEffect(() => {
    if (isCollapsed || (isCollapsed && !isHovered)) {
      removeActiveMenu();
    }
  }, [isCollapsed, isHovered]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

SidebarProvider.defaultProps = {
  menus: [],
  children: PropTypes.node.isRequired
};

SidebarProvider.propTypes = {
  menus: PropTypes.array.isRequired
};
